import { useEffect, useState, type ReactNode } from 'react';
import { Building2, Plus, Save, Warehouse, Phone } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import {
  useTerrainConfig, useUpdateTerrainConfig,
  useSites, useCreateSite, useUpdateSite,
  useAires, useCreateAire, useUpdateAire,
} from '@/hooks/useTerrainAdmin';
import type { Site, AireStockage } from '@/api/terrain-admin';
import { Badge, Modal, ModalHeader } from './_ui';

/**
 * SIMG-TERRAIN-2026-001 — Page "Paramètres".
 *
 * 3 sections empilées :
 *   - Configuration générale (numéro DAF + libellé — remplace nano config.json)
 *   - Sites (CRUD)
 *   - Aires de stockage (CRUD + seuils capacité)
 */
export const ParametresPage = () => {
  return (
    <>
      <Topbar
        title="Paramètres du portail terrain"
        subtitle="Configuration, sites et aires de stockage"
      />
      <div className="ds-content" style={{ display: 'grid', gap: 20 }}>
        <SectionConfig />
        <SectionSites />
        <SectionAires />
      </div>
    </>
  );
};

// ─── Section 1 : Configuration ─────────────────────────────────

function SectionConfig() {
  const { data, isLoading } = useTerrainConfig();
  const update = useUpdateTerrainConfig();
  const [tel, setTel]   = useState('');
  const [lib, setLib]   = useState('');

  useEffect(() => {
    if (data) {
      setTel(data.contactTel ?? '');
      setLib(data.contactLibelle ?? '');
    }
  }, [data]);

  const modifie = data && (tel !== (data.contactTel ?? '') || lib !== (data.contactLibelle ?? ''));

  const submit = () => {
    update.mutate({
      contactTel:     tel.trim() || null,
      contactLibelle: lib.trim() || null,
    });
  };

  return (
    <div className="ds-panel" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <Phone size={20} style={{ color: 'var(--pr-1)' }} />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Contact affiché sur le portail terrain</h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--tx-2)', margin: '0 0 16px 0' }}>
        Visible sur l'écran « Lien manquant » et sur les messages d'erreur — pour que l'intervenant puisse
        joindre son responsable. Modification effective au prochain lancement de l'app terrain.
      </p>

      {isLoading && <div style={{ color: 'var(--tx-3)' }}>Chargement…</div>}

      {!isLoading && (
        <>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
            <Champ label="Numéro (format international)" value={tel} onChange={setTel} placeholder="+2250700000000" mono />
            <Champ label="Comment on le nomme à l'écran" value={lib} onChange={setLib} placeholder="ex. le DAF LEADBETON" />
          </div>
          {data?.modifieLe && (
            <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 8 }}>
              Dernière modification : {new Date(data.modifieLe).toLocaleString('fr-FR')}
            </div>
          )}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="ds-btn ds-btn-p" onClick={submit} disabled={!modifie || update.isPending}>
              {update.isPending ? 'Enregistrement…' : <><Save size={14} /> Enregistrer</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Section 2 : Sites ─────────────────────────────────────────

function SectionSites() {
  const { data: sites = [], isLoading } = useSites();
  const [showCreate, setShowCreate] = useState(false);
  const update = useUpdateSite();

  return (
    <div className="ds-panel" style={{ padding: 20 }}>
      <SectionHeader
        icon={<Building2 size={20} />}
        title="Sites"
        subtitle="Chaque intervenant est rattaché à un site (usine, dépôt, chantier)"
        action={
          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> Nouveau site
          </button>
        }
      />
      {isLoading && <div style={{ color: 'var(--tx-3)' }}>Chargement…</div>}
      {!isLoading && sites.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun site.</p>
      )}
      {sites.length > 0 && (
        <table className="ds-tbl">
          <thead>
            <tr>
              <th>Code</th><th>Libellé</th><th>Statut</th><th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s: Site) => (
              <tr key={s.id}>
                <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{s.code}</td>
                <td>{s.libelle}</td>
                <td>
                  <Badge tone={s.actif ? 'success' : 'neutral'}>
                    {s.actif ? 'Actif' : 'Désactivé'}
                  </Badge>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="ds-btn ds-btn-g ds-btn-sm"
                    onClick={() => update.mutate({ id: s.id, patch: { actif: !s.actif } })}
                  >
                    {s.actif ? 'Désactiver' : 'Réactiver'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showCreate && <SiteDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function SiteDialog({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState('');
  const [lib, setLib]   = useState('');
  const create = useCreateSite();

  const submit = () => {
    if (code.trim().length === 0 || lib.trim().length === 0) return;
    create.mutate({ code: code.trim().toUpperCase(), libelle: lib.trim() }, { onSuccess: onClose });
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 460, width: '100%', padding: 20 }}>
        <ModalHeader titre="Nouveau site" onClose={onClose} />
        <div style={{ display: 'grid', gap: 12 }}>
          <Champ label="Code court (unique, majuscules)" value={code} onChange={setCode} placeholder="ex. BKE, YAM" mono />
          <Champ label="Libellé" value={lib} onChange={setLib} placeholder="ex. Usine de Bouaké" />
        </div>
        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={submit} disabled={!code || !lib || create.isPending}>
            {create.isPending ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Section 3 : Aires de stockage ─────────────────────────────

function SectionAires() {
  const { data: sites = [] } = useSites();
  const [siteFilter, setSiteFilter] = useState('');
  const { data: aires = [], isLoading } = useAires(siteFilter || undefined);
  const [showCreate, setShowCreate] = useState(false);
  const [edit, setEdit] = useState<AireStockage | null>(null);

  return (
    <div className="ds-panel" style={{ padding: 20 }}>
      <SectionHeader
        icon={<Warehouse size={20} />}
        title="Aires de stockage"
        subtitle="Capacité par matière — utilisée pour les indicateurs rouge/orange/vert dans l'onglet Stock du portail terrain"
        action={
          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> Nouvelle aire
          </button>
        }
      />

      <div style={{ marginBottom: 12 }}>
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid var(--bd-2)', borderRadius: 6, fontSize: 13, background: 'var(--bg-1)' }}
        >
          <option value="">Tous les sites</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.libelle}</option>)}
        </select>
      </div>

      {isLoading && <div style={{ color: 'var(--tx-3)' }}>Chargement…</div>}
      {!isLoading && aires.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>
          Aucune aire{siteFilter ? ' pour ce site' : ''}. Les seuils du portail terrain ne s'afficheront pas tant que les capacités ne sont pas saisies.
        </p>
      )}
      {aires.length > 0 && (
        <table className="ds-tbl">
          <thead>
            <tr>
              <th>Site</th><th>Article</th><th>Capacité (kg)</th><th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {aires.map((a) => (
              <tr key={a.id}>
                <td>{a.site.code}</td>
                <td style={{ fontFamily: 'monospace' }}>{a.articleCode}</td>
                <td style={{ fontFamily: 'monospace', textAlign: 'right' }}>{Number(a.capaciteKg).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setEdit(a)}>Modifier</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && <AireDialog sites={sites} onClose={() => setShowCreate(false)} />}
      {edit && <AireEditDialog aire={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

function AireDialog({ sites, onClose }: { sites: Site[]; onClose: () => void }) {
  const [siteId, setSiteId] = useState(sites[0]?.id ?? '');
  const [code, setCode]     = useState('');
  const [cap, setCap]       = useState('');
  const create = useCreateAire();

  const submit = () => {
    const capNum = Number(cap.replace(/[^0-9]/g, ''));
    if (!siteId || !code || !capNum || capNum <= 0) return;
    create.mutate({ siteId, articleCode: code.trim(), capaciteKg: String(capNum) }, { onSuccess: onClose });
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 460, width: '100%', padding: 20 }}>
        <ModalHeader titre="Nouvelle aire de stockage" onClose={onClose} />
        <div style={{ display: 'grid', gap: 12 }}>
          <ChampSelect label="Site" value={siteId} onChange={setSiteId} options={sites.map((s) => ({ value: s.id, label: `${s.code} — ${s.libelle}` }))} />
          <Champ label="Code article (matière première)" value={code} onChange={setCode} placeholder="ex. MP-GRA-515" mono />
          <Champ label="Capacité en kg" value={cap} onChange={setCap} placeholder="ex. 70000" mono />
        </div>
        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AireEditDialog({ aire, onClose }: { aire: AireStockage; onClose: () => void }) {
  const [cap, setCap] = useState(aire.capaciteKg);
  const update = useUpdateAire();

  const submit = () => {
    const capNum = Number(cap.replace(/[^0-9]/g, ''));
    if (!capNum || capNum <= 0) return;
    update.mutate({ id: aire.id, patch: { capaciteKg: String(capNum) } }, { onSuccess: onClose });
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 400, width: '100%', padding: 20 }}>
        <ModalHeader titre={`Modifier capacité · ${aire.site.code} · ${aire.articleCode}`} onClose={onClose} />
        <Champ label="Capacité en kg" value={cap} onChange={setCap} mono />
        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={submit} disabled={update.isPending}>
            {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Primitives partagées ──────────────────────────────────────

function SectionHeader({ icon, title, subtitle, action }: {
  icon: ReactNode; title: string; subtitle: string; action?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: 'var(--pr-1)' }}>{icon}</span>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h2>
        </div>
        <p style={{ fontSize: 13, color: 'var(--tx-2)', margin: '4px 0 0 30px' }}>{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

// Modal + ModalHeader importés depuis ./_ui (React portal + gestion Escape)

function Champ({ label, value, onChange, placeholder, mono }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>{label}</div>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{
          width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8,
          fontSize: 14, color: 'var(--tx-1)', background: 'var(--bg-1)',
          fontFamily: mono ? 'monospace' : 'inherit',
        }}
      />
    </label>
  );
}

function ChampSelect({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>{label}</div>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8,
          fontSize: 14, color: 'var(--tx-1)', background: 'var(--bg-1)',
        }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
